import type { CompanySettings } from "@repo/app-types/entities/company";
import type { PredefinedTeammateRoleType } from "@repo/app-types/entities/teammatePermissions";
import {
  allTeammateRoleTypes,
  isPredefinedTeammateRoleType,
  PermissionActions,
  PermissionSubjects,
  predefinedRoleNameByType,
} from "@repo/app-types/entities/teammatePermissions";
import { isValidDomain } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { CreatableComboboxField } from "@repo/ui/combobox/creatableComboboxField";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { type FC, useEffect, useState } from "react";
import { useUpdateCompanyMutation } from "../../../api/entities/company.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  fetchCompanyDomains,
  selectCompanyDomainsState,
  updateCompanyDomains,
} from "../../company/companyDomainsSlice.js";
import { selectCompany } from "../../company/companySlice.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";

const roleOptions = allTeammateRoleTypes
  .filter(isPredefinedTeammateRoleType)
  .map((role) => ({
    label: predefinedRoleNameByType[role],
    value: role,
  }));

export const SignInSettingsTab: FC = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const [updateCompany] = useUpdateCompanyMutation();

  const {
    domains: companyDomains,
    status: companyDomainsLoadingState,
    error: companyDomainsError,
  } = useAppSelector(selectCompanyDomainsState);

  const persistedDomains = companyDomains || [];
  const persistedRoleRaw =
    company?.settings.domain_signup_teammate_permissions.role.type;
  const persistedRole: PredefinedTeammateRoleType | undefined =
    persistedRoleRaw && isPredefinedTeammateRoleType(persistedRoleRaw)
      ? persistedRoleRaw
      : undefined;
  const persistedMagicLinkAuthEnabled =
    company?.settings.magic_link_auth_enabled ?? false;

  const [localDomains, setLocalDomains] = useState<
    readonly string[] | undefined
  >(undefined);
  const [localRole, setLocalRole] = useState<
    PredefinedTeammateRoleType | undefined
  >(undefined);
  const [localMagicLinkAuthEnabled, setLocalMagicLinkAuthEnabled] = useState<
    boolean | undefined
  >(undefined);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchCompanyDomains());
  }, [dispatch]);

  if (!company) {
    return null;
  }

  const hasDomainsChanged =
    localDomains !== undefined &&
    JSON.stringify(localDomains) !== JSON.stringify(persistedDomains);
  const hasRoleChanged = localRole !== undefined && localRole !== persistedRole;
  const hasMagicLinkAuthChanged =
    localMagicLinkAuthEnabled !== undefined &&
    localMagicLinkAuthEnabled !== persistedMagicLinkAuthEnabled;
  const hasAnyChanges =
    hasDomainsChanged || hasRoleChanged || hasMagicLinkAuthChanged;

  const saveAll = async () => {
    setIsSaving(true);
    try {
      const settingsPatch: Partial<CompanySettings> = {};
      if (hasRoleChanged) {
        settingsPatch.domain_signup_teammate_permissions = {
          role: { type: localRole },
        };
      }
      if (hasMagicLinkAuthChanged) {
        settingsPatch.magic_link_auth_enabled = localMagicLinkAuthEnabled;
      }

      const operations: Promise<unknown>[] = [];
      if (hasDomainsChanged) {
        operations.push(dispatch(updateCompanyDomains(localDomains)).unwrap());
      }
      if (Object.keys(settingsPatch).length > 0) {
        operations.push(
          updateCompany({
            settings: settingsPatch,
          }).unwrap(),
        );
      }

      // TODO: Better error state if one fails and one succeeds
      await Promise.all(operations);

      showNotification(dispatch, {
        id: `sign-in-settings-updated-${new Date().getTime()}`,
        primaryMessage: "Sign in settings saved",
        type: NotificationType.SUCCESS,
      });
    } catch (err) {
      console.error("Error saving sign in settings:", err);
      showNotification(dispatch, {
        id: `sign-in-settings-updated-${new Date().getTime()}`,
        primaryMessage: "Failed to save sign in settings",
        type: NotificationType.FAILURE,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (companyDomainsLoadingState === "loading") {
    return (
      <div className="py-4">
        <DefaultSkeleton />
      </div>
    );
  }

  return (
    <div className="py-4">
      <Can
        I={PermissionActions.UPDATE}
        a={PermissionSubjects.COMPANY_SETTINGS}
        passThrough={true}
      >
        {(allowed) => (
          <div className="flex flex-col gap-8">
            <section className="flex flex-col gap-4">
              <h3 className="text-base font-semibold text-foreground">
                Approved sign-up domains
              </h3>
              <div>
                <CreatableComboboxField
                  label="Approved email domains"
                  placeholder="Add a domain (e.g. acme.com)"
                  value={localDomains ?? persistedDomains}
                  onChange={(domains) => {
                    setLocalDomains(
                      domains.filter((domain) => isValidDomain(domain)),
                    );
                  }}
                  description="Users who sign up with a matching email domain will be
              able to join your organization without a direct invite.
              To add more than one domain, separate each with a comma."
                  errorMessage={companyDomainsError || undefined}
                  isDisabled={!allowed}
                />
              </div>

              {persistedRole ? (
                <div>
                  <SimpleSelect
                    label="Role for teammates that join via matching email domain"
                    value={localRole ?? persistedRole}
                    onChange={(option) => setLocalRole(option)}
                    options={roleOptions}
                    isDisabled={!allowed}
                  />
                </div>
              ) : null}
            </section>

            <section className="flex flex-col gap-2">
              <h3 className="text-base font-semibold text-foreground">
                Email sign-in
              </h3>
              <Checkbox
                isChecked={
                  localMagicLinkAuthEnabled ?? persistedMagicLinkAuthEnabled
                }
                onChange={(isChecked) =>
                  setLocalMagicLinkAuthEnabled(isChecked)
                }
                label="Allow teammates to sign in with an email link"
                isDisabled={!allowed}
              />
              <div className="text-sm text-muted-foreground">
                When enabled, teammates who are already invited or approved can
                sign in by entering their work email address and clicking a
                one-time link delivered to their inbox.
              </div>
            </section>

            <div>
              <Button
                variant={ButtonVariant.Primary}
                onClick={() => void saveAll()}
                isDisabled={!allowed || !hasAnyChanges || isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        )}
      </Can>
    </div>
  );
};
